import React from 'react'
import styles from './MapV2.scss'
import { GoogleMapEngine } from 'common/MapV2/engines/GoogleMapEngine'
import { MapboxMapEngine } from 'common/MapV2/engines/MapboxMapEngine'
import { softError } from 'lib/softError'
import { MapFocusContext } from 'common/Map/MapFocusContext'
import { OverlayPicker } from 'common/MapV2/controls/OverlayPicker'
import { Viewport } from 'data/records'
import { checkWebGLSupport } from 'lib/utils'
import { tileServices } from 'lib/tileServices'

// do this once, at boot
const supportsWebGL = checkWebGLSupport() && !window.location.search.includes('FAKE_NO_WEBGL')

const getMapEngine = (overlay) => {
  if (!supportsWebGL) {
    // fallback to gmaps rendering if WebGL isn't supported
    return GoogleMapEngine
  }
  // otherwise, use gmaps only for google overlay types
  switch (overlay.type) {
    case 'google':
      return GoogleMapEngine
    default:
      return MapboxMapEngine
  }
}

export class MapV2 extends React.PureComponent {
  static defaultProps = {
    initialViewport: { center: { lat: 45.5355945, lng: -122.6609456 }, zoom: 13 },
    overlay: tileServices.rwgps,
    scrollWheelZoom: true
  }

  constructor (props) {
    super(props)
    this.state = {
      focusContext: {
        focusId: null,
        setFocus: this.setFocus
      }
    }
    // this doesn't go in state because changes to it should NOT cause re-renders.
    // it's a bookkeeping property to ensure that things don't change if a
    // re-render causes the underlying map engine to remount.
    this._lastViewport = props.initialViewport
  }

  onPickOverlay = (overlay) => {
    this.props.onChangeOverlay && this.props.onChangeOverlay(overlay)
  }

  setFocus = (focusId) => {
    const focusContext = {
      focusId,
      setFocus: this.setFocus
    }
    this.setState({ focusContext })
  }

  onMapClick = (e) => {
    this.setFocus(null)
    this.props.onMapClick && this.props.onMapClick(e)
  }

  onViewportChanged = (newViewport) => {
    this.props.onViewportChanged && this.props.onViewportChanged(newViewport)
    this._lastViewport = newViewport
  }

  render () {
    const {
      children,
      initialViewport,
      initialViewportPadding,
      overlay,
      scrollWheelZoom,
      showBikePaths,
      onClickFullscreen,
      controls,
      hideOverlayPicker,
      ...props
    } = this.props
    const { focusContext } = this.state
    // initialViewport can either be a {bounds: [{latLng}, {latLng}]}
    // object, OR it can be a {center: {latLng}, zoom: number}
    // object.
    if (!Viewport.validate(initialViewport)) {
      softError('validateViewport: bounds and center/zoom are mutually exclusive, you have specified both.')
    }

    const divProps = Object.assign({}, props)
    delete divProps.onMapClick // causes unknown prop warning

    const MapEngine = getMapEngine(overlay)
    return (
      <div {...divProps} className={styles.MapV2}>
        <MapFocusContext.Provider value={focusContext}>
          <MapEngine
            ref={r => { this._mapEngine = r }}
            overlay={overlay}
            scrollWheelZoom={scrollWheelZoom}
            showBikePaths={showBikePaths}
            onMapClick={this.onMapClick}
            initialViewport={this._lastViewport}
            initialViewportPadding={initialViewportPadding}
            onViewportChanged={this.onViewportChanged}>
            {children}
          </MapEngine>
          {controls}
          {!hideOverlayPicker && <OverlayPicker overlay={overlay} onPickOverlay={this.onPickOverlay} />}
        </MapFocusContext.Provider>
      </div>
    )
  }
}
